import { Text } from '../../atoms/Text/Text';
import styles from './FilterChipCounter.module.css';

/**
 * Figma: `_filter chip counter` (44:521, sole variant `Property 1=Default`
 * 265:7530) — the count badge inside `Filter Chip`'s `Text and Badge` frame.
 *
 * The file has no generic `Counter` component set left: node 44:521, which used
 * to hold `Counter` with its `Type=Alert|Action|Info|Neutral` axis, was rebuilt
 * in place as this single-variant chip badge and moved onto the Filter Chips
 * page. The 2026-09-02 sync completed that — the four `component/counter/*`
 * tokens have now been deleted from the file, so the `Counter` atom this once
 * sat beside has been retired. This is the only count badge in the system.
 *
 * It is a rounded rectangle (`radius/xs`) on `component/chip/counter-surface`
 * (gray/200) under `text/primary`. Until 2026-09-02 it borrowed
 * `component/badge/info-surface` under `text/link`; the designer has since given
 * it a token of its own and recoloured it neutral.
 *
 * The set's annotation is stale — it is the old `Counter` note, still listing
 * four `Type` rows and claiming `radius/full` and `font-weight/medium`. The
 * live variant binds `radius/xs` and draws Inter Regular, and none of the four
 * fills in that table exist any more. The bindings are followed here. See
 * plan.md §7.
 */
export type FilterChipCounterProps = {
  count: number;
  /** Counts above this render as "{max}+". */
  max?: number;
  /** Accessible description, e.g. "Brand filters". */
  label?: string;
  className?: string;
};

export function FilterChipCounter({ count, max = 99, label, className }: FilterChipCounterProps) {
  const display = count > max ? `${max}+` : String(count);

  return (
    <span
      className={[styles.counter, className].filter(Boolean).join(' ')}
      aria-label={label ? `${count} ${label}` : undefined}
    >
      <Text size="xs" leading="none" weight="regular" color="primary">
        {display}
      </Text>
    </span>
  );
}
